import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, type LinkProps } from '@tanstack/react-router'
import { type ReactNode, useState } from 'react'
import { type Meeting, type MeetingScope, MeetingVisibilityChip } from '@/entities/meeting'
import { companyProjectsQueryOptions } from '@/entities/project'
import { CreateMeetingDialog } from '@/features/create-meeting'
import { DeleteMeetingDialog, EditMeetingDialog } from '@/features/edit-meeting'
import { flattenPages } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { DateStamp } from '@/shared/ui/date-stamp'
import { EmptyState } from '@/shared/ui/empty-state'
import { FileRow } from '@/shared/ui/file-row'
import { Icon } from '@/shared/ui/icon'
import { Menu } from '@/shared/ui/menu'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'
import { ZoneNote } from '@/shared/ui/zone-badge'
import { meetingFeedQueryOptions, type TelemostMeeting } from '../api/feed'

interface MeetingListProps {
  scope: MeetingScope
  isEmployee: boolean
  /** Ссылка на страницу своей встречи — роутинг остаётся у страницы. */
  meetingLink: (meetingId: string) => LinkProps
  /** Показывать строку «Проект» (контур компании). */
  showProject?: boolean
}

// Единая лента встреч контура: заведённые в deal и проекция телемоста, слитые
// по времени. Своя встреча ведёт на свою страницу; встреча телемоста своей
// страницы не имеет — внешняя ссылка в новой вкладке с пометкой «из телемоста».
export function MeetingList({ scope, isEmployee, meetingLink, showProject }: MeetingListProps) {
  const feedQ = useQuery(meetingFeedQueryOptions(scope))
  // Имена проектов для строки «Проект» — только когда она показывается.
  const projectsQ = useInfiniteQuery({
    ...companyProjectsQueryOptions(scope.kind === 'company' ? scope.id : ''),
    enabled: scope.kind === 'company' && Boolean(showProject),
  })
  const [createOpen, setCreateOpen] = useState(false)

  if (feedQ.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (feedQ.error) {
    return <StatusScreen title="Не удалось загрузить встречи" message="Обновите страницу." />
  }

  // Элемент без своего объекта рисовать нечего — такие строки ленты отбрасываем.
  const items = feedQ.data.filter((item) => item.own ?? item.telemost)
  const projectById = new Map(
    (projectsQ.data ? flattenPages(projectsQ.data.pages) : []).map((p) => [p.id, p]),
  )
  const projectRow = (projectId: string | null | undefined): ReactNode => {
    if (!showProject) return null
    if (!projectId) return <MetaLine label="Проект" value="напрямую компании" />
    const project = projectById.get(projectId)
    if (!project) return <MetaLine label="Проект" value="—" />
    return (
      <p className="text-sm text-text-secondary">
        <span className="text-text-tertiary">Проект: </span>
        <Link
          to="/$company/$project"
          params={{ company: project.company_slug, project: project.slug }}
          className="transition-colors hover:text-accent hover:underline"
        >
          {project.title}
        </Link>
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="label">Встречи</span>
          {/* Время встречи уезжает в календарь событием — зона названа явно,
              один раз на ленту, а не тегом у каждой карточки. */}
          <ZoneNote what="Время встреч" />
        </div>
        {isEmployee && (
          <Button size="sm" variant="secondary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={16} />
            Встреча
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="Встреч нет"
          description={
            isEmployee
              ? 'Заведите встречу — встречи из телемоста появятся здесь сами.'
              : 'Здесь появятся встречи, открытые вам командой проекта.'
          }
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((item) =>
            item.own ? (
              <OwnCard
                key={item.own.id}
                meeting={item.own}
                isEmployee={isEmployee}
                meetingLink={meetingLink}
                projectRow={projectRow(item.own.project_id)}
              />
            ) : item.telemost ? (
              <TelemostCard
                key={item.telemost.id}
                call={item.telemost}
                isEmployee={isEmployee}
                projectRow={projectRow(item.telemost.project_id)}
              />
            ) : null,
          )}
        </ul>
      )}

      {createOpen && (
        <CreateMeetingDialog scope={scope} open onClose={() => setCreateOpen(false)} />
      )}
    </div>
  )
}

// Каркас карточки: иконка-плитка · заголовок с пометками · время справа,
// затем контентный столбец.
function MeetingCard({
  icon,
  title,
  marks,
  when,
  actions,
  children,
}: {
  icon: 'calendar' | 'users'
  title: ReactNode
  marks?: ReactNode
  when: ReactNode
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <li className="rounded-card bg-bg-surface p-4 ring-1 ring-border-subtle ring-inset">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-control bg-bg-elevated text-text-tertiary">
          <Icon name={icon} size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{title}</span>
              {marks}
            </div>
            <span className="flex shrink-0 items-center gap-1">
              {when}
              {actions}
            </span>
          </div>
          {children}
        </div>
      </div>
    </li>
  )
}

function MetaLine({ label, value }: { label: string; value: string }) {
  return (
    <p className="text-sm text-text-secondary">
      <span className="text-text-tertiary">{label}: </span>
      {value}
    </p>
  )
}

function ExternalLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="mt-0.5 inline-flex w-fit items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
    >
      <Icon name="external-link" size={14} />
      {label}
    </a>
  )
}

function OwnCard({
  meeting,
  isEmployee,
  meetingLink,
  projectRow,
}: {
  meeting: Meeting
  isEmployee: boolean
  meetingLink: (meetingId: string) => LinkProps
  projectRow: ReactNode
}) {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const hasBody = Boolean(
    projectRow ||
      meeting.location ||
      meeting.participants ||
      meeting.url ||
      meeting.notes ||
      meeting.attachments.length,
  )

  return (
    <>
      <MeetingCard
        icon="calendar"
        title={
          <Link
            {...meetingLink(meeting.id)}
            className="text-sm font-medium text-text-primary transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
          >
            {meeting.title}
          </Link>
        }
        marks={isEmployee ? <MeetingVisibilityChip visible={meeting.customer_visible} /> : null}
        when={<DateStamp value={meeting.when} className="shrink-0 text-xs text-text-tertiary" />}
        actions={
          isEmployee ? (
            <Menu
              align="end"
              aria-label={`Действия со встречей «${meeting.title}»`}
              trigger={<Icon name="dots" size={16} />}
              triggerClassName="p-1 text-text-tertiary"
              items={[
                { label: 'Изменить', icon: 'pencil', onSelect: () => setEditOpen(true) },
                {
                  label: 'Удалить',
                  icon: 'close',
                  danger: true,
                  onSelect: () => setDeleteOpen(true),
                },
              ]}
            />
          ) : null
        }
      >
        {hasBody && (
          <div className="mt-2 flex flex-col gap-1.5">
            {projectRow}
            {meeting.location && <MetaLine label="Где" value={meeting.location} />}
            {meeting.participants && <MetaLine label="Участники" value={meeting.participants} />}
            {meeting.notes && (
              <p className="whitespace-pre-wrap text-sm text-text-secondary">{meeting.notes}</p>
            )}
            {meeting.url && <ExternalLink href={meeting.url} label="Ссылка на встречу" />}
            {meeting.attachments.length > 0 && (
              <div className="mt-1 flex flex-col gap-1.5">
                {meeting.attachments.map((f) => (
                  <FileRow key={f.key} file={f} />
                ))}
              </div>
            )}
          </div>
        )}
      </MeetingCard>

      {editOpen && <EditMeetingDialog meeting={meeting} open onClose={() => setEditOpen(false)} />}
      {deleteOpen && (
        <DeleteMeetingDialog
          meeting={{ id: meeting.id, title: meeting.title }}
          open
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </>
  )
}

// Фаза встречи телемоста словами. Словарь источника: незнакомое значение
// показываем как есть, а не роняем строку.
function phaseLabel(phase: string): { label: string; tone: 'neutral' | 'accent' } {
  switch (phase) {
    case 'live':
      return { label: 'идёт', tone: 'accent' }
    case 'ended':
      return { label: 'завершена', tone: 'neutral' }
    case 'scheduled':
      return { label: 'запланирована', tone: 'neutral' }
    default:
      return { label: phase, tone: 'neutral' }
  }
}

// Когда была (будет) встреча телемоста: план, иначе фактические времена.
function whenLine(call: TelemostMeeting): { label: string; from?: string; to?: string } {
  if (call.phase === 'ended') {
    if (call.started_at && call.ended_at) {
      return { label: 'шла', from: call.started_at, to: call.ended_at }
    }
    const at = call.ended_at ?? call.started_at ?? call.scheduled_at
    return { label: 'завершена', ...(at ? { from: at } : {}) }
  }
  if (call.phase === 'live') {
    const at = call.started_at ?? call.scheduled_at
    return { label: 'идёт с', ...(at ? { from: at } : {}) }
  }
  return { label: 'начало', ...(call.scheduled_at ? { from: call.scheduled_at } : {}) }
}

// Встреча из телемоста: своей страницы в deal у неё нет — владелец сущности
// источник, поэтому ни правки, ни удаления, только внешние ссылки. Их выбирает
// бэкенд под смотрящего (заказчику — его контур, guest_url — никому): `url` —
// страница встречи в телемосте, `join_url` — вход, и он приезжает, только пока
// звонок идёт. До и после звонка ведём на страницу: входить некуда.
function TelemostCard({
  call,
  isEmployee,
  projectRow,
}: {
  call: TelemostMeeting
  isEmployee: boolean
  projectRow: ReactNode
}) {
  const phase = phaseLabel(call.phase)
  const when = whenLine(call)
  const hasBody = Boolean(
    projectRow || call.description || call.organizer || call.join_url || call.url,
  )

  return (
    <MeetingCard
      icon="users"
      title={call.title || 'Встреча без названия'}
      marks={
        <>
          <Chip tone={phase.tone}>{phase.label}</Chip>
          <span className="text-xs text-text-tertiary">из телемоста</span>
          {isEmployee && <MeetingVisibilityChip visible={call.customer_visible} />}
        </>
      }
      when={
        <span className="flex shrink-0 flex-wrap items-center justify-end gap-1 text-xs text-text-tertiary">
          <span>{when.label}</span>
          {when.from && <DateStamp value={when.from} className="text-xs text-text-tertiary" />}
          {when.to && (
            <>
              <span aria-hidden="true">—</span>
              <DateStamp value={when.to} className="text-xs text-text-tertiary" />
            </>
          )}
        </span>
      }
    >
      {hasBody && (
        <div className="mt-2 flex flex-col gap-1.5">
          {projectRow}
          {call.description && (
            <p className="whitespace-pre-wrap text-sm text-text-secondary">{call.description}</p>
          )}
          {call.organizer && <MetaLine label="Организатор" value={call.organizer} />}
          {call.join_url ? (
            <ExternalLink href={call.join_url} label="Войти во встречу" />
          ) : call.url ? (
            <ExternalLink href={call.url} label="Открыть в телемосте" />
          ) : null}
        </div>
      )}
    </MeetingCard>
  )
}
